import React, { useEffect, useState } from 'react';
import { FileBrowser } from '../../../../components/storage';
import { useStorageListing } from '../hooks/useStorageListing';

interface S3Config {
  bucketName: string;
  accessKey: string;
  secretKey: string;
  endpoint?: string;
  baseDir: string;
}

interface Props {
  config: S3Config;
  onChange: (config: S3Config) => void;
  qumulo?: boolean;
  onToggleQumulo: (qumulo: boolean) => void;
  selectedPath: string | null;
  onPathChange: (path: string) => void;
  disabled: boolean;
}

export function S3Source({ config, onChange, qumulo = false, onToggleQumulo, selectedPath, onPathChange, disabled }: Props) {
  // The listing only runs once the user clicks Connect (or navigates a directory).
  const [connected, setConnected] = useState(false);

  // Switching AWS ↔ Qumulo rebuilds the source, so require a fresh Connect.
  useEffect(() => { setConnected(false); }, [qumulo]);

  const provider = qumulo ? 's3qumulo' : 's3';
  const listingParams: Record<string, string | number | boolean> = {
    bucketName: config.bucketName,
    accessKey: config.accessKey,
    secretKey: config.secretKey,
    baseDir: config.baseDir,
  };
  if (qumulo) listingParams.endpoint = config.endpoint ?? '';

  const { data, isFetching, refetch } = useStorageListing(provider, listingParams, connected);

  const set = (key: keyof S3Config, value: any) => onChange({ ...config, [key]: value });

  const connect = () => {
    setConnected(true);
    refetch();
  };

  const navigate = (path: string) => {
    onChange({ ...config, baseDir: path });
    refetch();
  };

  const levelUp = () => {
    const parts = config.baseDir.split('/').filter(Boolean);
    parts.pop();
    navigate(parts.join('/'));
  };

  return (
    <div>
      <form onSubmit={e => { e.preventDefault(); connect(); }} autoComplete="off" noValidate>
        <h4>
          <button type="submit" className="btn btn-primary btn-xs" disabled={isFetching || disabled}>
            <i className={`fas ${isFetching ? 'fa-spinner fa-spin' : 'fa-bolt'}`} aria-hidden="true" /> Connect
          </button>

          <div className="btn-group pull-right">
            <button
              type="button"
              className={`btn btn-default btn-xs${!qumulo ? ' active' : ''}`}
              onClick={() => onToggleQumulo(false)}
              disabled={disabled}
            >
              AWS
            </button>
            <button
              type="button"
              className={`btn btn-default btn-xs${qumulo ? ' active' : ''}`}
              onClick={() => onToggleQumulo(true)}
              disabled={disabled}
            >
              Qumulo
            </button>
          </div>
        </h4>

        <div className="form-group">
          {qumulo && (
            <div className="row" style={{ marginBottom: 10 }}>
              <div className="col-md-6">
                <input
                  type="text"
                  placeholder="Endpoint"
                  className="form-control"
                  value={config.endpoint ?? ''}
                  onChange={e => set('endpoint', e.target.value)}
                  maxLength={255}
                  disabled={disabled}
                />
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-md-4">
              <input
                type="text"
                placeholder="Bucket Name"
                className="form-control"
                value={config.bucketName}
                onChange={e => set('bucketName', e.target.value)}
                maxLength={255}
                disabled={disabled}
              />
            </div>
            <div className="col-md-4">
              <input
                type="text"
                placeholder="Access Key"
                className="form-control"
                value={config.accessKey}
                onChange={e => set('accessKey', e.target.value)}
                maxLength={255}
                disabled={disabled}
              />
            </div>
            <div className="col-md-4">
              <input
                type="password"
                autoComplete="new-password"
                placeholder="Secret Key"
                className="form-control"
                value={config.secretKey}
                onChange={e => set('secretKey', e.target.value)}
                maxLength={255}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      </form>

      <FileBrowser
        pathLabel={config.baseDir}
        files={data ?? []}
        loading={isFetching}
        selectedPath={selectedPath}
        onSelect={onPathChange}
        onNavigate={navigate}
        onLevelUp={levelUp}
      />
    </div>
  );
}

export function defaultS3Config(): S3Config {
  return { bucketName: '', accessKey: '', secretKey: '', baseDir: '' };
}
